import { DateTime, IANAZone } from 'luxon';

// TODO: Přidat namespace, aby nebyla kolize s Iterator rozhraním!
// - https://stackoverflow.com/questions/51525227/how-to-prevent-shadowing-global-interface
// - https://github.com/Microsoft/TypeScript-Handbook/blob/master/pages/Declaration%20Merging.md
// - https://github.com/microsoft/TypeScript-Handbook/blob/master/pages/Modules.md
// TODO: Reformátovat odsazení.
// export namespace Deterministic {

type Opaque<T, K> = T & { __opaque__: K };

export type Duration = Opaque<number, 'Duration'>;
export type Time = Opaque<number, 'Time'>;

export const MinTime: Time = 0 as Time; // FIXME: Nechat Jen MinTime nebo UnixEpoch nebo obojí?
export const UnixEpoch: Time = 0 as Time; // TODO: Refaktorovat výskyty.
export const MaxTime: Time = 9007199254740991 as Time; // 2^53 - 1 kvůli omezení JS

// FIXME
export const Second: Duration = 1000000 as Duration;

// FIXME: Konvence pro pojmenování set/change metod immutable like struktur? Nebo předělat na static? Nebo nejsou nějaké settery nepoužívané?

export class Clip<T> {
  readonly content: T;
  readonly duration: Duration;
  readonly volume: number;

  constructor(content: T, duration: Duration, volume: number) {
    this.content = content;
    this.duration = duration;
    this.volume = volume;
  }

  public copySetDuration(duration: Duration): Clip<T> {
    return new Clip<T>(this.content, duration, this.volume);
  }

  // TODO: Nepoužívá se.
  public copySetVolume(volume: number): Clip<T> {
    return new Clip<T>(this.content, this.duration, volume);
  }
}

export class Spot<T> {
  readonly time: Time;
  readonly content: T;
  readonly duration: Duration;
  readonly volume: number;

  constructor(time: Time, content: T, duration: Duration, volume: number) {
    this.time = time;
    this.content = content;
    this.duration = duration;
    this.volume = volume;
  }

  public static fromClip<T>(time: Time, clip: Clip<T>) {
    return new Spot<T>(time, clip.content, clip.duration, clip.volume);
  }

  public setDuration(duration: Duration): Spot<T> {
    return new Spot<T>(this.time, this.content, duration, this.volume);
  }

  public setVolume(volume: number): Spot<T> {
    return new Spot<T>(this.time, this.content, this.duration, volume);
  }

  public to(): Time {
    return (this.duration > 0)
      ? this.time + this.duration as Time
      : this.time;
  }
}

export interface KeyedClipIteratorFactory<T, K> extends ClipIteratorFactory<T> {
  // Unikátní klíč pro detekci cyklů a podporu globálních iterátory bez nutnosti singletonů.
  key(): K;
}

export interface ClipIteratorFactory<T> extends Validator {
  get global(): boolean; // FIXME: Přejmenovat global na něco příznačnějšího, třeba statefulBetweenRewinds? Stateful obecně je špatně, protože i neglobální iterátor má stav.
  newIterator(t: Time): ClipIterator<T>;
}

export interface ClipIterator<T> {
  next(t: Time): Clip<T> | null;
  rewind(t: Time): void;
  shouldRewind(): boolean;
}

export class Playlist<T> implements ClipIteratorFactory<T> {
  tracks: Track<T>[];

  constructor(...tracks: Track<T>[]) {
    this.tracks = tracks;
  }

  get global(): boolean { return false; }

  newIterator(t: Time): ClipIterator<T> {
    return new PlaylistIterator<T>(this.tracks);
  }

  valid(t: Time): boolean {
    for (let i = 0; i < this.tracks.length; i++) {
      const track = this.tracks[i];
      const valid = track.valid(t);
      if (!valid) {
        continue;
      }
      if ((track.content as ClipIteratorFactory<T>).newIterator !== undefined) {
        const ritf = track.content as ClipIteratorFactory<T>;
        // Content je iterátor (resp. továrna). Když je podle tracku validní,
        // tak to musíme každopádně rekurzivně potvrdit, protože v případě
        // "zkratu" by prvotní ověření jen podle tzv. médiaplánu nemuselo
        // být pravdivé.
        const rvalid = ritf.valid(t);
        if (rvalid) {
          return true;
        }
      } else {
        if (track.duration <= (0 as Duration)) {
        // Content není iterátor, ale má nekladnou délku, takže se neuvažuje.
          continue;
        }
        return true;
      }
    }
    return false;
  }

  currentOrNextValid(t: Time, maxTime: Time): Time {
    let valid: boolean = false;
    let nextValid: Time = maxTime;
    for (let i = 0; i < this.tracks.length; i++) {
      const track = this.tracks[i];
      let next = track.currentOrNextValid(t, maxTime);

      // Pokud je next >= maxTime, tak je každopádně track nevalidní
      // a již se nemusí rekurzivně ověřovat.
      if (next >= maxTime) {
        continue;
      }

      valid = next <= t;
      if ((track.content as ClipIteratorFactory<T>).newIterator !== undefined) {
        const ritf = track.content as ClipIteratorFactory<T>;
        // Content je iterátor (resp. továrna). Když je podle tracku validní,
        // tak to musíme každopádně rekurzivně potvrdit, protože v případě
        // "zkratu" by prvotní ověření jen podle tzv. médiaplánu nemuselo
        // být pravdivé.

        // V případě že iterátor validní, tak ověříme od času t, jinak od next.
        let rt = t;
        if (!valid) {
          rt = next; // TODO: Zakomentování řádku nefailuje alespoň jeden test, takže není správně pokryto testy!
        }
        const rnext = ritf.currentOrNextValid(rt, maxTime);

        // Pokud je iterátor rekurzivně nevalidní (tj. je ve zkratu),
        // tak se vrátí tento rekurzivní výsledek, jinak původní médiaplánový.
        const rvalid = rnext <= rt;
        if (!rvalid) {
          valid = rvalid;
          next = rnext;
        }
      } else if (track.duration <= (0 as Duration)) {
        // Content není iterátor, ale má nekladnou délku, takže se neuvažuje.
        continue;
      }

      if (valid) {
        return t;
      }
      nextValid = Math.min(nextValid, next) as Time;
    }
    return nextValid;
  }
}

class PlaylistIterator<T> implements ClipIterator<T> {
  protected tracks: Track<T>[]; // Musí se zacházet jako s immutable, kvůli kontraktu v new Playlist().
  private i: number;

  constructor(tracks: Track<T>[]) {
    this.tracks = tracks;
    this.i = 0;
  }

  next(t: Time): Clip<T> | null {
    if (this.shouldRewind()) {
      return null;
    }

    // Hledáme nějaký validní track od pozice it.i do konce.
    const l = this.tracks.length;
    for (; this.i < l; this.i++) {
      // Zjistíme, zda je track validní.
      const track = this.tracks[this.i];
      const valid = track.valid(t);
      if (valid) {
        break;
      }
    }

    // Pokud i >= l, tak žádný validní track nebyl nalezen a ukončujeme iteraci.
    // TODO: Stejné jako ShouldRewind nahoře - nahradit zde a nahoře vymazat, protože smyčka for se v takovém stavu ihned ukončí, aby se to neduplikovalo?
    if (this.i >= l) {
      return null;
    }
    const track = this.tracks[this.i];
    this.i++;

    // Vrátíme nalezený track jako clip, kde ok bylo určeno výše.
    return track.getClip();
  }

  rewind(t: Time): void {
    // FIXME: this.reset(this.itf);
    this.i = 0;
  }

  shouldRewind(): boolean {
    return this.i >= this.tracks.length; // TODO: Větší než nemůže nastat, odstranit?
  }
}

export class ShufflePlaylist<T> extends Playlist<T> implements ClipIteratorFactory<T> {
  constructor(...tracks: Track<T>[]) {
    super(...tracks);
  }

  newIterator(t: Time): ClipIterator<T> {
    return new ShufflePlaylistIterator<T>(this.tracks, t);
  }
}

class ShufflePlaylistIterator<T> extends PlaylistIterator<T> {
  private unshuffledTracks: Track<T>[];

  constructor(unshuffledTracks: Track<T>[], t: Time) {
    // Zde stačí jen prealokace pole tracků bez inicializace,
    // jelikož rewind je při zamíchání nakopíruje z unshuffledTracks.
    const tracks: Track<T>[] = [];
    tracks.length = unshuffledTracks.length;
    super(tracks);
    this.unshuffledTracks = unshuffledTracks;
    this.rewind(t);
  }

  override rewind(t: Time): void {
    // Předpokládá, že volání z konstruktoru již předalokovalo pole tracků a inside-out
    // varianta Fisher-Yates shuffle algoritmu tracky nakopíruje z unshuffledTracks:
    // https://en.wikipedia.org/wiki/Fisher%E2%80%93Yates_shuffle#The_%22inside-out%22_algorithm
    // TODO: Pozor, že 32b PRNG stačí jen pro max. 12 tracků, aby mohlo být
    // dosaženo každé permutace: https://en.wikipedia.org/wiki/Fisher%E2%80%93Yates_shuffle#Pseudorandom_generators
    super.rewind(t);
    const tracks = this.tracks;
    const tl = this.unshuffledTracks.length;
    if (tl > 0) {
      // Seed stavu 32b PRNG pomocí XORování MSB a LSB bitů 53b času.
      let state = ((t / 0x00200000 /*Math.pow(2, 53-32)*/) ^ t) >>> 0; // FIXME: Co s tím, že čas teoreticky může být i záporný, jak pak deterministicky převést čas na seed?

      // Optimalizace rozbalením smyčky pro i := 0 původního algoritmu.
      // Ušetří jedno generování pseudo-random čísla.
      // FIXME: I když tento loop unroll není bezvadný, protože aby byl
      // transparentní, tak by zde ještě muselo být:
      // state = (state + 0x9e3779b9) >>> 0;
      tracks[0] = this.unshuffledTracks[0];
      for (let i = 1; i < tl; i++) {
        // Nejlepší známý 32b PRNG generátor s 32b stavem:
        // - https://gist.github.com/tommyettinger/46a874533244883189143505d203312c
        // - https://github.com/skeeto/hash-prospector/issues/19#issuecomment-1120105785
        // TODO: Podle tohoto autora by v JS mělo stačit coercovat 32b signed hodnoty,
        // takže pro jednotnost zůstává state unsingned 32b a r signed 32b s tím,
        // že před použití r je potřeba udělat r >>> 0 pro získání unsigned 32b čísla:
        // - https://gist.github.com/tommyettinger/46a874533244883189143505d203312c?permalink_comment_id=4577493#gistcomment-4577493
        state = (state + 0x9e3779b9) >>> 0;
        let r = state;
        r ^= r >>> 16;
        r = Math.imul(r, 0x21f0aaad);
        r ^= r >>> 15;
        r = Math.imul(r, 0x735a2d97);
        r ^= r >>> 15;
        
        // Rychlý výběr indexu v rozsahu [0, i] bez modulo operace:
        // - https://arxiv.org/abs/1805.10941
        // - https://lemire.me/blog/2019/06/06/nearly-divisionless-random-integer-generation-on-various-systems/
        // Potřebovali bychom 64b celočíselnou aritmetiku, což jaksi JS nemá :-(
        // Takže musíme emulovat pomocí double aritmetiky.
        // TODO: Optimalizovat dělení konstantou na násobení konstantou?
        const j = (((r >>> 0) * (i+1)) / 0x100000000) >>> 0;
        // FIXME: Test nekopírování na sebe samu se dá vynechat,
        // když je levné kopírování.
        if (true || j !== i) {
          tracks[i] = tracks[j];
        }
        tracks[j] = this.unshuffledTracks[i];
      }
    }
  }
}

// FIXME: Řešit zero hodnotu, nullable src a detekci cyklů?
export class WindowWithCountIteratorFactory<T> implements ClipIteratorFactory<T> {
  private count: number;
  private src: ClipIteratorFactory<T>; // FIXME: Nullable?

  constructor(count: number, src: ClipIteratorFactory<T>) {
    // Neslušně ošetření záporných hodnot.
    this.count = count >= 0 ? count : 0;

    // Detekce a "ošetření" cyklu, jinak dojde ke stack overflow!
    // TODO: Zatím takto neslušně (myšleno tak, že volající se o tom nedozví).
    // this.src = src != this ? src : null; // FIXME: Může to v TS/JS vůbec nastat?
    this.src = src;
  }

  get global(): boolean { return false; }

  newIterator(t: Time): ClipIterator<T> {
    return new WindowWithCountIterator<T>(this.src, this.count);
  }

  valid(t: Time): boolean {
    return this.src.valid(t);
  }

  currentOrNextValid(t: Time, maxTime: Time): Time {
    return this.src.currentOrNextValid(t, maxTime);
  }
}

// FIXME: https://stackoverflow.com/a/68201883
// FIXME: https://github.com/microsoft/TypeScript/issues/32211
// FIXME: https://stackoverflow.com/questions/72591870/how-to-define-a-generic-inner-class
// FIXME: https://www.typescriptlang.org/play?#code/MYGwhgzhAECCA8AVAfNA3gWAFDV9CALmAQJbDQBC0AvNKJDBUqpjnu8APYB2hATgFdgBTnwAUABwEAjEGWgS+nCQC5oiAJTps2duwC+uvIba5FnAgFNhlgCbRpQgNaWCEAPxrYfPmACe8LAAdEwoyADcOlgm3GAAtpYQEmDAlnDaptCWAB4SogTQBH4SaaGotACSvETcqYjFlvBFJZwAZnAhzMjYJlFc1dCcXp3cAnHSlnzl0NyWAO4dFGIAssQAFkEAChUa4dAA9PuDTv7Y-RCcIJZBIJwA5mKcQeYSQSIAYiTZdmIATBq7A5HABEAGYggBGAAswKAA
class WindowWithCountIterator<T> implements ClipIterator<T> {
  private itf: ClipIteratorFactory<T>;
  private count: number;

  private it: ClipIterator<T> | null;
  private i: number;

  constructor(itf: ClipIteratorFactory<T>, count: number) {
    this.itf = itf;
    this.count = count;

    this.it = null;
    this.i = 0;
  }

  next(t: Time): Clip<T> | null {
    if (this.shouldRewind()) {
      return null;
    }

    if (this.it == null) {
      this.it = this.itf.newIterator(t);
    } else if (this.it.shouldRewind()) {
      this.it.rewind(t);
    }

    const count = this.count;
    let c = this.it.next(t);
    if (c === null) {
      if (this.it.shouldRewind()) {
        this.it.rewind(t);
      }

      // Pokud druhý pokus selže, tak jsme ve zkratu a ukončíme se
      // a nastavíme i na count, aby případné další volání Next
      // splnilo kontrakt.
      c = this.it.next(t);
      if (c === null) {
        // TODO: Je toto předčasné záměrné ukončení nyní sémanticky nutné po zapracování ShouldRewind a Rewind? Protože pokud by se asi nedělalo, tak při zkratu a zavolání Next by se provedlo 2x neúspěšné Next, takže de facto taky zásek.
        if (count > 0) {
          this.i = count; // Co když je count nulový (zápornost je vyloučena v konstruktoru)? Tak pak bychom ho neměli používat... Ale hlavně i při nekonečném iterátoru musíme při zkratu se vynořit a to kupodivu "perzistentně", aby další volání Next hlásilo ended.
        } else {
          this.i = -1; // FIXME: Zkusíme nastavit na zápornou hodnotu pro signalizaci, že je konec, a případný Rewind to zase vynuluje a tím pádem aktivuje.
        }
        return null;
      }
    }

    if (count > 0) {
      this.i++;
    }
    return c;
  }

  rewind(t: Time): void {
    // FIXME: Go specific komentář.
    // U globálního iterátoru se nesmí v rewindu resetovat vše (samozřejmě kromě
    // zachování vlastností z továrny) jako u neglobálních, ale je potřeba navíc
    // zachovat nějaký stav navíc mezi rewindy, což je v tomto případě it.it.
    this.i = 0;
  }

  shouldRewind(): boolean {
    // TODO: c < 0 by nemělo nastat, jelikož se ošetřuje v konstruktoru.
    const c = this.count;
    return (c > 0 && this.i == c) || (c <= 0 && this.i < 0);
  }
}

export class GlobalFactory<T> implements ClipIteratorFactory<T> {
  private src: ClipIteratorFactory<T>;

  constructor(src: ClipIteratorFactory<T>) { this.src = src; }

  static wrap<T>(src: ClipIteratorFactory<T>): ClipIteratorFactory<T> {
    return src.global ? src : new GlobalFactory(src);
  }

  get global(): boolean { return true; }

  newIterator(t: Time): ClipIterator<T> { return this.src.newIterator(t); }

  valid(t: Time): boolean { return this.src.valid(t); }

  currentOrNextValid(t: Time, maxTime: Time): Time { return this.src.currentOrNextValid(t, maxTime); }
}

// FIXME: Teoreticky by se mělo implementovat kompozicí GlobalFactory a WindowsWithCountIteratorFactory,
// ale u OOP jazyků je jednoduší v tomto případě podědit a overridovat global.
export class MagazinePlaylist<T> extends WindowWithCountIteratorFactory<T> {
  constructor(count: number, src: ClipIteratorFactory<T>) {
    super(count, src);
  }

  override get global(): boolean { return true; }
}

export class Track<T> implements Validator {
  readonly content: T;
  readonly duration: Duration;
  readonly volume: number;
  readonly validator: Validator | null;

  constructor(content: T, duration: Duration, volume: number, validator?: Validator | null) {
    this.content = content;
    this.duration = duration;
    this.volume = volume;
    this.validator = validator ?? null;
  }

  public getClip(): Clip<T> { // FIXME: Nebo jen clip podle konvence v Go?
    return new Clip(this.content, this.duration, this.volume);
  }

  public valid(t: Time): boolean {
    return this.validator?.valid(t) ?? true;
  }

  public currentOrNextValid(t: Time, maxTime: Time): Time {
    return this.validator?.currentOrNextValid(t, maxTime) ?? t;
  }
}

export interface Validator {
  valid(t: Time): boolean;
  currentOrNextValid(t: Time, maxTime: Time): Time;
}

class ClipIteratorAndFactory<T, K> {
  readonly it: ClipIterator<T>;
  readonly itf: KeyedClipIteratorFactory<T, K>;

  constructor(it: ClipIterator<T>, itf: KeyedClipIteratorFactory<T, K>) {
    this.it = it;
    this.itf = itf;
  }
}

export class Simulator<T, K> {
  private itf: KeyedClipIteratorFactory<T, K> | null; // FIXME: Povolit null kvůli zero hodnotě?
  private t: Time; // TODO: Předělat na read-only getter.
  private maxTime: Time; // FIXME: Zero hodnota?
  private stack: Spot<ClipIteratorAndFactory<T, K>>[];
  private heap: Map<K, ClipIterator<T>> | null; // Klíčem je hodnota z KeyedClipIteratorFactory.key().

  constructor(itf: KeyedClipIteratorFactory<T, K> | null, t: Time, maxTime: Time) {
    // this.reset(itf, t, maxTime);

    this.itf = itf;
    this.t = t; // TODO: Test na rozsah.
    this.maxTime = maxTime;
    this.stack = new Array();
    this.heap = null;
  }

  public static zero<T, K>(): Simulator<T, K> {
    return new Simulator(null, 0 as Time, 0 as Time);
  }

  // TODO: Pokročilosti s pooly, zatím jen nejjednodušší varianta, výkon nevýkon.
  // FIXME: Reset?
  // reset(itf: ClipIteratorFactory<T>, t: Time) {
  //   this.itf = itf;
  //   this.t = t; // TODO: Test na rozsah.
  //   this.stack = new Array();
  //   this.heap = null;
  // }

  public getTime(): Time {
    return this.t;
  }

  public next(): Spot<T | null> | null {
    if (this.itf === null || this.t >= this.maxTime) { // FIXME: Jak se správně dělá test na null?
      return null;
    }

    for (; this.t < this.maxTime;) {
      if (this.stack.length === 0) {
        this.stackPush(this.itf, 0 as Duration, 1);
      }

      // Vynoření, pokud měl iterátor duration a ta již uplynula.
      const frame = this.stackPeek();
      if (frame.duration > 0 && this.t >= frame.to()) { // FIXME: Ta druhá podmínka, zda má mít i rovná se?
        this.stackDrop();
        continue;
      }

      const it = frame.content.it;
      let c = it.next(this.t);
      if (c === null) {
        if (this.stack.length === 1) {
          // Pokud se čas neposunul, došlo ke "zkratu" a je potřeba určit čas "odzkratování".
          if (this.t <= frame.time) {
            let nextValid = this.itf.currentOrNextValid(this.t, this.maxTime);

            // Normalizace nextValid <= maxTime.
            if (nextValid > this.maxTime) {
              nextValid = this.maxTime;
            }

            // Po dobu zkratu vložíme klip s prázdným obsahem.
            const invalidDuration = (nextValid - this.t) as Duration;
            const spot = new Spot<T | null>(this.t, null, invalidDuration, 0);

            // TODO: DRY s kódem níže, např. vyčlenění spotu a příznaku ok před.
            this.t = nextValid; // (this.t + invalidDuration) as Time;
            this.stackDrop();

            return spot;
          }
        }

        this.stackDrop();
        continue;
      }

      // Hlasitost je potřeba pronásobit hlasitostí na aktuální úrovni zanoření.
      // FIXME: Vlastně zde není kopie tracku hodnotou, takže se to mění přímo v Playlistu!
      // Kopie info, jelikož ho budeme měnit.
      // TODO: Pokud je hlasitost 1, tak by se nemuselo klonovat. Totéž pro ořez duration. Ale pak by se muselo složitě sledovat, kdy clone provést, aby se to neudělalo zbytečně 2x.
      c = c.copySetVolume(c.volume * frame.volume);

      // Zpracování podle druhu contentu.
      if ((c.content as ClipIteratorFactory<T>).newIterator !== undefined) {
        if ((c.content as KeyedClipIteratorFactory<T, K>).key !== undefined) {
          const kitf = c.content as KeyedClipIteratorFactory<T, K>;
          this.stackPush(kitf, c.duration, c.volume);
          continue;
        } else {
          throw new Error('ClipIteratorFactory must be KeyeClipdIteratorFactory'); // FIXME
        }
      } else {
        // Content tracku je médium, šablona nebo deterministický playlist, tj.
        // není to zanořený playlist. Pokud má nekladnou délku, tak se přeskočí.
        if (c.duration > 0) {
          if (frame.duration > 0) {
            c = c.copySetDuration((Math.min(this.t + c.duration as Time, frame.to()) - this.t) as Duration);
          }
          const spot = Spot.fromClip(this.t, c);
          this.t = this.t + c.duration as Time;
          return spot;
        }
      }
    }
    return null;
  }

  private stackPeek(): Spot<ClipIteratorAndFactory<T, K>> {
    return this.stack[this.stack.length-1];
  }

  private stackPush(itf: KeyedClipIteratorFactory<T, K>, duration: Duration, volume: number) {
    // Detekce zacyklení iterátorů, resp. jejich továren.
    const k = itf.key();
    for (const frame of this.stack) {
      if (frame.content.itf.key() === k) {
        throw new Error("zacykleno"); // FIXME: Dokumentovat, hláška, atd.
      }
    }

    let it: ClipIterator<T> | null = null;
    const global = itf.global;
    if (global)
    {
      // Když se nenalezne, díky ?? bude it null.
      it = this.heap?.get(k) ?? null;
      // Pro globální může být ve stavu, kdy se musí přetočit, aby další volání Next mohlo uspět.
      if (it !== null && it.shouldRewind()) {
        it.rewind(this.t);
      }
    }
    if (it === null) {
      it = itf.newIterator(this.t);
      if (global) {
        // Lazy alokace mapy, protože 80% simulací bude bez globálních iterátorů.
        if (this.heap === null) {
          this.heap = new Map(); // FIXME: make(map[IteratorFactory[T]]Iterator[T], 8) // TODO: Stanovit vhodnou velikost, aby nedocházelo k realokacím.
        }

        this.heap.set(k, it);
      }
    }

    this.stack.push(new Spot(
      this.t,
      new ClipIteratorAndFactory(it, itf),
      duration,
      volume));
  }

  private stackDrop() {
    const itaf = this.stackPeek().content;
    this.stack.pop();

    // Nyní se globální iterátor po celou životního cyklu simulátoru nikdy neuvolňuje,
    // takže do poolu se vrací jen neglobální iterátory.
    //
    // TODO: Kdysi byla idea, že by šlo iterátor trvale (možná i dočasně) ve zkratu
    // uvolnit a opětovně v době odblokování zase případně lazy vytvořit. Ale k tomu
    // je potřeba mít identitu původní továrny použitou k vytvoření iterátoru.
    //
    // Kdysi totiž byly marné pokusy skrze zrušenou metodu Iterator.IteratorFactory()
    // vracející továrnu, což ale kvůli nepravé dědičnosti nemuselo vracet původní
    // továrnu předanou v iteratorGet, takže se pak celý mechanismus heapu rozpadnul,
    // např. se neustále alokovaly nové nebo se nesprávně vrátily do poolu odkazované
    // globální iterátory!
    if (!itaf.itf.global) {
      // TODO: poolPut(itaf.it);
    }
  }
}

type EpochResult = {
  current: Time;
  next: Time;
};

export type EpochFunc = (t: Time) => EpochResult

function defaultEpoch(_: Time): EpochResult {
  return { current: MinTime, next: MaxTime };
}

function epoch(d: Duration): EpochFunc {
  return (t: Time): EpochResult => {
    const current = (euclidDiv(t, d) * d) as Time;
    const next = current + d as Time;
    return { current, next };
  };
}

// TODO: duration konstanty
export const hourEpoch = epoch(3600 * 1000000 as Duration);
export const dayEpoch = epoch(24 * 3600 * 1000000 as Duration); // TODO: Hrubě, bez podpory časových zón a půlnoci.

// https://github.com/tc39/proposal-integer-and-modulus-math
function euclidDiv(x: number, y: number): number {
  let q = Math.trunc(x / y); // TODO: Sémantika a výkon odseknutí desetinných míst, např. Math.trunc: https://stackoverflow.com/questions/4228356/how-to-perform-an-integer-division-and-separately-get-the-remainder-in-javascr
  const r = x - y * q;
  if (r < 0) {
    if (y < 0) {
      q++
    } else {
      q--
    }
  }
  return q;
}

// TODO: Implementační detail v rozhraní? Ale prakticky lze jednoduše změnit na string, ale otázka, zda by nebylo neustálé parsování na úkor výkonu.
// TODO: Kdo zaručí, že i všivé zařízení budou mít všechny timezony? Nebude potřeba polyfill Intl?
export function dayLocalEpoch(zone: IANAZone): EpochFunc {
  if (!zone.isValid) {
    throw new Error("zone is not valid"); // FIXME
  }
  return (t: Time): EpochResult => {
    const tc = DateTime.fromMillis(t / 1000, { zone }).startOf('day'); // FIXME: Necelé číslo, useknout, zaokrouhlit?
    const tn = tc.plus({ days: 1 });
    const current = tc.toMillis() * 1000 as Time;
    const next = tn.toMillis() * 1000 as Time;
    return { current, next };
  };
}

export function daySystemLocalEpoch(t: Time): EpochResult {
  const current = new Date(t / 1000);
  current.setHours(0, 0, 0, 0);
  const next = new Date(current);
  next.setDate(next.getDate() + 1);
  return { current: current.getTime() * 1000 as Time, next: next.getTime() * 1000 as Time };
}

export class SimulatorAt<T, K> {
  itf: KeyedClipIteratorFactory<T, K> | null; // FIXME: Null kvůli zero hodnotě?
  epoch: EpochFunc | null;
  spot: Spot<T | null> | null;
  s: Simulator<T | null, K>;

  constructor(itf: KeyedClipIteratorFactory<T, K> | null, epoch: EpochFunc | null) {
    this.itf = itf;
    this.epoch = epoch;
    this.spot = null; // FIXME: Spot.zero();
    this.s = Simulator.zero(); // TODO: Nebo null hodnota, aby itf uvnitř nemuselo být null?
  }

  public static zero<T, K>(): SimulatorAt<T, K> {
    return new SimulatorAt(null, null);
  }

  public getAt(t: Time): Spot<T | null> | null {
    if (this.itf === null || t >= MaxTime) {
      return null
    }

    // Získání epochy s defaultem při null funkci.
    const {current: ec, next: en} = (this.epoch ?? defaultEpoch)(t);

    // Hledání spotu od uloženého dále do budoucna.
    while (true) {
      // FIXME: Ošetření případů, kdy je potřeba resetovat simulátor,
      // včetně zero stavu, kdy má spot čas a délku 0:
      // - dotaz na jinou epochu, než do které patří uložený spot
      // - dotaz na čas v aktuální epoše, ale před čas uloženého spotu
      //   (tj. vracení zpět v čase)
      if (this.spot === null || ec >= this.spot.to() || t < this.spot.time) {
        // this.s.reset(this.itf, ec, en);
        this.s = new Simulator(this.itf, ec, en); // FIXME
      } else {
        // Toto zároveň řeší i vracení stejného spotu před jeho vypršením.
        // TODO: Tím pádem je vlastně možné v rámci jednoho spotu i nemonotónně skákat zpět, aniž by rewindovalo? Ověřit v testech a případně zdokumentovat.
        const to = this.spot.to();
        if (t < to) {
          // Ošetření případu, kdy spot je přes hranici epochy,
          // takže je potřeba oříznout, aby se epochy nepřekrývaly.
          if (to > en) {
            this.spot = this.spot.setDuration((en - this.spot.time) as Duration);
          }
          return this.spot;
        }
      }
      // Získání dalšího spotu ze simulátoru.
      this.spot = this.s.next(); 
      if (this.spot === null) {
        return null;
      }
    }
  }
}

// }
